/**
 * การ์ดมอนสเตอร์ (GDD หัวข้อ 6) — ช่องการ์ด การใส่ และผลรวม
 * **รอบ 18: ผู้เล่นเห็นเป็น "ไฟตะเกียง / ประกายวิญญาณ" ใส่ใน "ช่องตะเกียง"** โค้ดและ id ยังชื่อ card เดิม
 *
 * - ใส่การ์ดลงช่องของอุปกรณ์ที่ตรงประเภท (`card.fits` ใน items.json) **ถอดไม่ได้**
 * - จำนวนช่องตามความหายาก แต่ไม่เกินเพดานของชนิดช่องตามตาราง GDD
 * - การ์ดผูกกับ "ช่องที่สวม" เหมือนระดับตีบวก (ไอเทมยังไม่มีตัวตนแยกชิ้น)
 *   **ถอดหรือเปลี่ยนของในช่องนั้น การ์ดหายไปด้วย** client ต้องถามยืนยันก่อน
 *
 * อยู่ใน shared เพราะ client ต้องคิดตามเพื่อแสดงค่าในหน้าต่างตัวละครให้ตรงกับดาเมจจริง
 * pure function ทั้งหมด server ใช้ตัดสินจริง
 */
import { mapEntries, readMap } from "./equip.js";
import { describeSkillMods, mergeSkillMods } from "./skills.js";
import { CLASSES } from "./data.js";
import { optionBonus, parseOptions } from "./options.js";
import { activeSetBonuses } from "./sets.js";
import { ELEMENT_NAMES, RACE_NAMES, STAT_KEYS, } from "./types.js";
/**
 * เพดานช่องตะเกียง (ช่องการ์ดเดิม) ของแต่ละชนิดช่อง — รอบ 18 "ไฟตะเกียงแทนการ์ด"
 * อาวุธสองมือมากสุด (3) เพราะใส่มือรองไม่ได้ · อาวุธมือเดียว 2 (+มือรอง 1 = เท่ากับสองมือ)
 * เสื้อของหายาก (epic ขึ้นไป) ได้ 2 · ที่เหลือ 1 · หน้ากาก ของพิเศษ สัตว์เลี้ยง ปีก ไม่มีช่อง
 */
export const CARD_SLOT_MAX = {
    weapon: 3, head: 1, offhand: 1, body: 2, legs: 1, feet: 1, ear: 1,
    face: 0, special: 0, pet: 0, wings: 0,
};
/** ช่องตะเกียงตามความหายาก แยกอาวุธมือเดียว/สองมือ/เสื้อ/อื่น ๆ (ก่อนหนีบด้วยเพดาน) */
export const RARITY_CARD_SLOTS = {
    weapon2: { common: 1, fine: 2, rare: 2, epic: 3, legend: 3 },
    weapon1: { common: 1, fine: 1, rare: 2, epic: 2, legend: 2 },
    body: { common: 1, fine: 1, rare: 1, epic: 2, legend: 2 },
    other: { common: 1, fine: 1, rare: 1, epic: 1, legend: 1 },
};
/** จำนวนช่องตะเกียง (ช่องการ์ด) ของอุปกรณ์ชิ้นนี้ */
export function cardSlotsOf(def) {
    if (!def || def.kind !== "equip" || !def.slot)
        return 0;
    const cap = CARD_SLOT_MAX[def.slot] ?? 0;
    const table = def.slot === "weapon" ? (def.hands === 2 ? "weapon2" : "weapon1") : def.slot === "body" ? "body" : "other";
    const want = def.cardSlots ?? RARITY_CARD_SLOTS[table][def.rarity ?? "common"];
    return Math.max(0, Math.min(cap, want));
}
/** การ์ดในช่องหนึ่ง เก็บเป็นสตริงคั่นด้วย "," (MapSchema<string> ซ้อน array ไม่ได้สะดวก) */
export function parseCards(raw) {
    return (raw ?? "").split(",").map((s) => s.trim()).filter(Boolean);
}
/** ธาตุของไฟตะเกียงแต่ละดวงในช่องหนึ่ง (เรียงตามลำดับที่ใส่) ใช้วาดไฟเรืองบนอุปกรณ์ */
export function sparkElements(raw, items) {
    return parseCards(raw).map((id) => items[id]?.card?.element ?? "neutral");
}
export const CARD_TEXT = {
    "empty-slot": "ยังไม่ได้สวมอะไรในช่องนี้",
    "not-card": "ไอเทมนี้ไม่ใช่ประกายวิญญาณ",
    "wrong-kind": "ประกายดวงนี้ใส่กับอุปกรณ์ชนิดนี้ไม่ได้",
    "wrong-class": "อาชีพนี้ใช้ประกายดวงนี้ไม่ได้",
    "no-slot": "อุปกรณ์ชิ้นนี้ไม่มีช่องตะเกียง",
    full: "ช่องตะเกียงของอุปกรณ์ชิ้นนี้เต็มแล้ว",
    "no-card": "ไม่มีประกายดวงนี้ในกระเป๋า",
};
/**
 * ใส่การ์ด `cardId` ลงของในช่อง `slot` ได้ไหม (ไม่ตรวจว่ามีการ์ดในกระเป๋า server ตรวจเอง)
 * @param equippedId ของที่สวมอยู่ในช่องนั้น
 * @param current การ์ดที่ใส่อยู่แล้วในช่องนั้น
 * @param classId อาชีพของผู้ใส่ ไม่ส่ง = ไม่ตรวจ `card.reqClass`
 */
export function insertDenial(items, equippedId, current, cardId, classId) {
    const gear = equippedId ? items[equippedId] : undefined;
    if (!gear || !gear.slot)
        return "empty-slot";
    const card = items[cardId];
    if (!card || card.kind !== "card" || !card.card)
        return "not-card";
    if (!card.card.fits.includes(gear.slot))
        return "wrong-kind";
    if (classId !== undefined && !cardClassOk(card, classId))
        return "wrong-class";
    const slots = cardSlotsOf(gear);
    if (slots === 0)
        return "no-slot";
    if (current.length >= slots)
        return "full";
    return undefined;
}
/** อาชีพนี้ใช้การ์ดใบนี้ได้ไหม (ไม่มี reqClass = ทุกอาชีพ) */
export function cardClassOk(card, classId) {
    const req = card?.card?.reqClass;
    return !req || req.length === 0 || req.includes(classId);
}
/**
 * ผลเสริมสกิลรวมจากของที่สวม (`bonus.skillMods`) และการ์ดในของเหล่านั้น
 * server ใช้ตอนร่าย client ใช้แสดงค่าในหน้าต่างสกิล
 */
export function gearSkillMods(equipped, cards, items, refines) {
    const srcs = [];
    for (const b of activeSetBonuses(equipped, refines))
        srcs.push(b.skillMods);
    for (const [slot, gearId] of mapEntries(equipped)) {
        if (!gearId)
            continue;
        srcs.push(items[gearId]?.bonus?.skillMods);
        for (const id of parseCards(readMap(cards, slot)))
            srcs.push(items[id]?.card?.bonus.skillMods);
    }
    return mergeSkillMods(...srcs);
}
/**
 * รวมผลของการ์ดทุกใบที่ใส่อยู่ในของที่สวมอยู่
 * และออปชันสุ่มของของที่สวม (`options` key = ช่อง ขั้น 3.6) เพราะใช้ฟิลด์ผลชุดเดียวกัน
 * และโบนัสชุดเซ็ต (ขั้น 3.5) เมื่อส่ง `refines` มา (โบนัสตีบวกร่วมเซ็ตต้องรู้ระดับตีบวก)
 */
export function cardTotals(equipped, cards, items, options, refines) {
    const out = { mods: {}, stats: {}, raceDmg: {}, elementRes: {} };
    const m = out.mods;
    for (const [slot, gearId] of mapEntries(equipped)) {
        if (!gearId)
            continue;
        for (const id of parseCards(readMap(cards, slot))) {
            const b = items[id]?.card?.bonus;
            if (b)
                addBonus(out, m, b);
        }
        if (options) {
            const opts = parseOptions(readMap(options, slot));
            if (opts.length)
                addBonus(out, m, optionBonus(opts));
        }
    }
    if (refines)
        for (const b of activeSetBonuses(equipped, refines))
            addBonus(out, m, b);
    return out;
}
function addBonus(out, m, b) {
    for (const k of STAT_KEYS) {
        const v = b.stats?.[k];
        if (v)
            out.stats[k] = (out.stats[k] ?? 0) + v;
    }
    if (b.hpMul)
        m.hpMul = (m.hpMul ?? 1) * b.hpMul;
    if (b.atkMul)
        m.atkMul = (m.atkMul ?? 1) * b.atkMul;
    if (b.matkMul)
        m.matkMul = (m.matkMul ?? 1) * b.matkMul;
    if (b.aspdMul)
        m.aspdMul = (m.aspdMul ?? 1) * b.aspdMul;
    if (b.flatDef)
        m.flatDef = (m.flatDef ?? 0) + b.flatDef;
    if (b.flatCrit)
        m.flatCrit = (m.flatCrit ?? 0) + b.flatCrit;
    if (b.flatHit)
        m.flatHit = (m.flatHit ?? 0) + b.flatHit;
    if (b.flatFlee)
        m.flatFlee = (m.flatFlee ?? 0) + b.flatFlee;
    for (const [r, v] of Object.entries(b.raceDmg ?? {})) {
        out.raceDmg[r] = (out.raceDmg[r] ?? 0) + v;
    }
    for (const [e, v] of Object.entries(b.elementRes ?? {})) {
        out.elementRes[e] = (out.elementRes[e] ?? 0) + v;
    }
}
/** ต้านธาตุรวมได้ไม่เกินนี้ กันใส่การ์ดซ้อนจนไม่เจ็บเลย */
export const ELEMENT_RES_CAP = 0.5;
/** ตัวคูณดาเมจที่ผู้เล่นทำใส่มอนเผ่านี้ */
export function raceDamageMul(fx, race) {
    return 1 + (race ? fx?.raceDmg[race] ?? 0 : 0);
}
/** ตัวคูณดาเมจที่ผู้เล่นรับจากมอนธาตุนี้ */
export function elementTakenMul(fx, element) {
    const res = element ? fx?.elementRes[element] ?? 0 : 0;
    return 1 - Math.min(ELEMENT_RES_CAP, Math.max(0, res));
}
const STAT_TH = {
    STR: "STR", AGI: "AGI", VIT: "VIT", INT: "INT", DEX: "DEX", LUK: "LUK",
};
const pct = (x) => `${Math.round(x * 100)}%`;
/** คำบรรยายผลของการ์ด สร้างจากข้อมูลเสมอ ข้อความจึงไม่มีวันไม่ตรงกับผลจริง */
export function describeCard(b, reqClass) {
    const parts = [];
    if (reqClass?.length)
        parts.push(`เฉพาะ${reqClass.map((c) => CLASSES[c]?.name ?? c).join("/")}`);
    for (const k of STAT_KEYS) {
        const v = b.stats?.[k];
        if (v)
            parts.push(`${STAT_TH[k]} +${v}`);
    }
    if (b.hpMul)
        parts.push(`HP สูงสุด +${pct(b.hpMul - 1)}`);
    if (b.atkMul)
        parts.push(`ATK +${pct(b.atkMul - 1)}`);
    if (b.matkMul)
        parts.push(`MATK +${pct(b.matkMul - 1)}`);
    if (b.aspdMul)
        parts.push(`ความเร็วโจมตี +${pct(b.aspdMul - 1)}`);
    if (b.flatDef)
        parts.push(`DEF +${b.flatDef}`);
    if (b.flatCrit)
        parts.push(`CRIT +${b.flatCrit}%`);
    if (b.flatHit)
        parts.push(`HIT +${b.flatHit}`);
    if (b.flatFlee)
        parts.push(`FLEE +${b.flatFlee}`);
    for (const [r, v] of Object.entries(b.raceDmg ?? {})) {
        parts.push(`ดาเมจใส่${RACE_NAMES[r]} +${pct(v)}`);
    }
    for (const [e, v] of Object.entries(b.elementRes ?? {})) {
        parts.push(`ต้านธาตุ${ELEMENT_NAMES[e]} ${pct(v)}`);
    }
    parts.push(...describeSkillMods(b.skillMods));
    return parts.join(" · ");
}
//# sourceMappingURL=cards.js.map